<template>
  <v-card>
    <v-card-title
      v-text="`History for ${entityType} ${targetName}`"
    />
    <v-card-text>
      <v-simple-table
        :loading="isLoading"
      >
        <thead>
          <tr>
            <th>Author</th>
            <th>Timestamp</th>
            <th>Change list</th>
          </tr>
        </thead>

        <tbody>
          <tr
            v-for="diff in diffList"
            :key="diff.created + diff.author"
          >
            <td v-text="usernameById[diff.author] || diff.author" />
            <td v-text="diff.created" />
            <td>
              <v-row
                v-for="change in diff.changes"
                :key="change.key + diff.created"
                class="mx-0"
              >
                  <strong>{{ change.key }}</strong>: {{ change.from }}->{{ change.to }}
              </v-row>
            </td>
          </tr>
        </tbody>
      </v-simple-table>
    </v-card-text>
  </v-card>
</template>

<script>
import { uniq, isEqual, difference } from 'lodash'
import { API } from '@/utils/api'

const DATA_CONFIG_BY_TYPE = {
  user: {
    url: '/update_history/user.json',
    paramName: 'user_id',
  },
  role: {
    url: '/update_history/role.json',
    paramName: 'role_id',
  },
}

export default {
  props: {
    entityType: {
      type: String,
      default: 'user',
    },
    targetId: {
      type: Number,
    },
    users: {
      type: Array,
      required: true,
    },
    rolesById: {
      type: Object,
      required: true,
    },
  },
  data() {
    return {
      historyEntries: [],
      isLoading: false,
    }
  },

  computed: {
    diffList() {
      return this.historyEntries.map(entry => ({
        author: entry.user_id,
        created: entry.created,
        changes: this.getHistoryDiff(entry)
      }))
    },

    usernameById() {
      const usernameById = {}
      for (const {user_id, name} of this.users) {
        usernameById[user_id] = name
      }

      return usernameById
    },

    targetName() {
      return this.entityType === 'user' ? this.usernameById[this.targetId] : `Role #${this.targetId} ${this.rolesById[this.targetId]?.name || ''}`
    },
  },

  methods: {
    getHistoryDiff({from, to, prefix = ''}) {
      const changes = []
      const prefixNormalized = prefix ? prefix + '.' : ''

      const allKeys = uniq(Object.keys(from).concat(Object.keys(to)))

      for (const key of allKeys) {
        if (isEqual(from[key], to[key])) {
          continue
        }

        if (typeof from[key] === 'object' && typeof to[key] === 'object') {
          if (Array.isArray(to[key])) {
            const elementsRemoved = difference(from[key], to[key])
            const elementsAdded = difference(to[key], from[key])
            if (elementsRemoved.length || elementsAdded.length) {
              changes.push({ key: prefixNormalized + key, from: elementsRemoved, to: elementsAdded })
            }
          } else {
            changes.push(...this.getHistoryDiff({ prefix: key, from: from[key], to: to[key] }))
          }
        } else {
          if (from[key] !== to[key]) {
            changes.push(({ key: prefixNormalized + key, from: from[key], to: to[key] }))
          }
        }
      }

      return changes
    },
    loadEntries() {
      this.isLoading = true

      return API.call(DATA_CONFIG_BY_TYPE[this.entityType].url, {
        [DATA_CONFIG_BY_TYPE[this.entityType].paramName]: this.targetId
      }).then(r => {
        this.historyEntries = r.data
      }).finally(() => {
        this.isLoading = false
      })
    },
  },

  watch: {
    targetId: {
      handler(id) {
        if (id) {
          this.historyEntries = []
          this.loadEntries()
        }
      },
      immediate: true,
    }
  }
}
</script>
